import {
    Fab,
    Grid,
    Popper,
    Fade,
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Divider,
    IconButton,
    FormControlLabel,
    Checkbox,
    Button,
    Typography,
    Tooltip,
    makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Replay5Icon from "@material-ui/icons/Replay5";
import clsx from "clsx";
import { useState } from "react";

import { OpenButton } from "@/utilities/types/streaming";

import useActionButtonStyles from "../ActionButton.styles";


const useStyles = makeStyles((theme) => {
    return {
        actions: {
            paddingTop: 20,
        },
        checkbox: {
            display: "block",
        },
        close: {
            position: "absolute",
            top: 8,
            right: 8,
            color: theme.palette.primary.contrastText,
        },
        disabledButton: {
            backgroundColor: "#555 !important", // Thanks material-ui!
        },
        divider: {
            backgroundColor: theme.palette.primary.light,
        },
        menu: {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.contrastText,
            borderRadius: 8,
        },
        popper: {
            zIndex: theme.zIndex.modal,
            marginBottom: 16,
            width: 380,
            marginRight: "0.625rem",
        },
    };
});

interface QuickDownloadButtonProps {
    currentCamera: { id: number; name: string };
    otherCameras: { id: number; name: string }[];
    onSubmit: (ids: number[]) => void;
    menuAnchor: HTMLElement | null;
    handleMenu: (button: OpenButton) => void;
    open: boolean;
}

const QuickDownloadButton: React.FC<QuickDownloadButtonProps> = ({
    currentCamera,
    otherCameras,
    onSubmit,
    menuAnchor,
    handleMenu,
    open,
}) => {
    const actionButtonClasses = useActionButtonStyles();
    const styles = useStyles();

    const [selectedCameras, setSelectedCameras] = useState<number[]>([currentCamera.id]);
    const [showTooltip, setShowTooltip] = useState(false);

    const id = open ? "popper" : undefined;

    const handleCheck = (checked: boolean, id: number) => {
        if (checked) {
            setSelectedCameras([...selectedCameras, id]);
        } else {
            const filtered = selectedCameras.filter((cam) => cam !== id);

            setSelectedCameras(filtered);
        }
    };

    const toggleAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setSelectedCameras([currentCamera.id, ...otherCameras.map((c) => c.id)]);
        } else {
            setSelectedCameras([]);
        }
    };

    const handleClick = () => {
        setShowTooltip(false);
        handleMenu(OpenButton.QuickDownload);
    };

    return (
        <>
            <Popper id={id} open={open} anchorEl={menuAnchor} disablePortal transition placement="top-end" className={styles.popper}>
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                        <Card raised classes={{ root: styles.menu }}>
                            <IconButton
                                id={"streaming_quick-download-button_close-button"}
                                data-testid="quick-download-button_close-button"
                                aria-label="close"
                                onClick={() => handleMenu(OpenButton.None)}
                                classes={{ root: styles.close }}
                            >
                                <CloseIcon />
                            </IconButton>
                            <CardHeader title="Capture Last 5 Minutes" />
                            <Divider classes={{ root: styles.divider }} />
                            <CardContent>
                                <Typography variant="h5">Request video from</Typography>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id={"streaming_quick-download-button_all-checkbox"}
                                            checked={selectedCameras.length === otherCameras.length + 1}
                                            name="toggle-all"
                                            onChange={toggleAll}
                                        />
                                    }
                                    label="All Live Unit Cameras"
                                />
                                <Divider classes={{ root: styles.divider }} />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            id={"streaming_quick-download-button_current-camera-checkbox"}
                                            checked={selectedCameras.indexOf(currentCamera.id) !== -1}
                                            name={currentCamera.name}
                                            onChange={(e) => handleCheck(e.target.checked, currentCamera.id)}
                                        />
                                    }
                                    label={`${currentCamera.name} (current camera)`}
                                    classes={{ root: styles.checkbox }}
                                />
                                {otherCameras.map((camera) => (
                                    <FormControlLabel
                                        key={camera.id}
                                        control={
                                            <Checkbox
                                                id={`streaming_quick-download-button_${camera.id}-checkbox`}
                                                checked={selectedCameras.indexOf(camera.id) !== -1}
                                                name={camera.name}
                                                onChange={(e) => handleCheck(e.target.checked, camera.id)}
                                            />
                                        }
                                        label={camera.name}
                                        classes={{ root: styles.checkbox }}
                                    />
                                ))}
                                <Divider classes={{ root: styles.divider }} />
                                <CardActions classes={{ root: styles.actions }}>
                                    <Button
                                        id={"streaming_quick-download-button_cancel-button"}
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => handleMenu(OpenButton.None)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        id={"streaming_quick-download-button_request-video-button"}
                                        variant="contained"
                                        color="secondary"
                                        disabled={!selectedCameras.length}
                                        onClick={() => {
                                            onSubmit(selectedCameras);
                                            handleMenu(OpenButton.None);
                                        }}
                                        classes={{ disabled: styles.disabledButton }}
                                    >
                                        Request Video
                                    </Button>
                                </CardActions>
                            </CardContent>
                        </Card>
                    </Fade>
                )}
            </Popper>
            <Grid item className={actionButtonClasses.fabItem}>
                <Tooltip
                    PopperProps={{ disablePortal: true }}
                    open={showTooltip}
                    disableHoverListener
                    onMouseEnter={() => !open && setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    title="Quick Download"
                    placement="top"
                >
                    <Fab
                        id={"streaming_quick-download-button"}
                        className={clsx(actionButtonClasses.fab, open && actionButtonClasses.active)}
                        aria-label="quick-download"
                        onClick={handleClick}
                        aria-describedby={id}
                    >
                        <Replay5Icon />
                    </Fab>
                </Tooltip>
            </Grid>
        </>
    );
};
export default QuickDownloadButton;
